import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { vendorsApi, projectsApi, evaluationsApi, api, type VendorResponse as ApiVendorResponse, type Evaluation } from '../lib/api'
import { Button } from '../components/ui/Button'
import { StatusBadge, StatusChip, Badge, type StatusVariant } from '../components/ui/Badge'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import FileViewer from '../components/FileViewer'
import { useChat } from '../hooks/useChat'
import { useUpdateEvaluation } from '../lib/hooks'
import { trackEvaluationEvidenceViewed } from '@/analytics/events'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ArrowLeft,
  ChevronDown,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  Upload
} from 'lucide-react'

interface FileItem {
  file_path: string
  file_name: string
  file_size: number
  file_type: string
}

// Markdown renderer component for AI assistant messages
const MarkdownRenderer = ({ content }: { content: string }) => (
  <div className="prose prose-sm max-w-none">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Headings
        h1: ({node, ...props}) => (
          <h1 className="text-2xl font-bold mt-6 mb-3 text-[#303030]" {...props} />
        ),
        h2: ({node, ...props}) => (
          <h2 className="text-xl font-bold mt-5 mb-2 text-[#303030]" {...props} />
        ),
        h3: ({node, ...props}) => (
          <h3 className="text-lg font-semibold mt-4 mb-2 text-[#303030]" {...props} />
        ),

        // Lists
        ul: ({node, ...props}) => (
          <ul className="list-disc pl-6 my-2 space-y-1" {...props} />
        ),
        ol: ({node, ...props}) => (
          <ol className="list-decimal pl-6 my-2 space-y-1" {...props} />
        ),
        li: ({node, ...props}) => (
          <li className="text-[#303030] leading-relaxed" {...props} />
        ),

        // Text formatting
        strong: ({node, ...props}) => (
          <strong className="font-semibold text-[#303030]" {...props} />
        ),
        em: ({node, ...props}) => (
          <em className="italic text-[#303030]" {...props} />
        ),
        p: ({node, ...props}) => (
          <p className="my-2 text-[#303030] leading-relaxed" {...props} />
        ),

        // Code
        code: ({node, inline, ...props}) =>
          inline
            ? <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
            : <code className="block bg-gray-100 p-3 rounded text-sm font-mono my-2 overflow-x-auto" {...props} />,

        // Links
        a: ({node, ...props}) => (
          <a className="text-[#7035c4] hover:underline" {...props} />
        ),

        // Blockquotes
        blockquote: ({node, ...props}) => (
          <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-2" {...props} />
        ),

        // Horizontal rules
        hr: ({node, ...props}) => (
          <hr className="my-4 border-gray-300" {...props} />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
)

// Evaluation result → tag chip appearance
type EvaluationResult = 'meets' | 'partially_meets' | 'not_met'
const RESULT_META: Record<string, { variant: StatusVariant; label: string }> = {
  meets: { variant: 'done', label: 'Yes' },
  partially_meets: { variant: 'warning', label: 'Partially' },
  not_met: { variant: 'error', label: 'No' },
  not_evaluated: { variant: 'neutral', label: 'N/A' },
}
const RESULT_OPTIONS: EvaluationResult[] = ['meets', 'partially_meets', 'not_met']

// Status shown as its colored tag chip, with a small chevron that opens an
// inline menu to change the result — keeps the tag look instead of a form field.
const StatusResultDropdown: React.FC<{
  value: string
  onChange: (value: EvaluationResult) => void
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const current = RESULT_META[value] || RESULT_META.not_evaluated

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex hover:opacity-80 transition-opacity"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <StatusChip variant={current.variant} className="cursor-pointer">
          {current.label}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </StatusChip>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[112px]">
          {RESULT_OPTIONS.map((option) => {
            const meta = RESULT_META[option]
            return (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option)
                  setOpen(false)
                }}
                className={`w-full flex items-center justify-center px-2 py-1.5 hover:bg-gray-50 ${
                  option === value ? 'bg-gray-50' : ''
                }`}
              >
                <StatusChip variant={meta.variant}>{meta.label}</StatusChip>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export const VendorResponseDetailPage: React.FC = () => {
  const { projectId, responseId } = useParams<{ projectId: string; responseId: string }>()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedFileIndex, setSelectedFileIndex] = useState(0)
  const [showFileDropdown, setShowFileDropdown] = useState(false)
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null)
  const [isLoadingFile, setIsLoadingFile] = useState(false)
  const [pdfHighlights, setPdfHighlights] = useState<Array<{
    id: string
    text: string
    startIndex: number
    length: number
    page: number
  }>>([])
  const [isClosing, setIsClosing] = useState(false)
  // Single-open accordion: id of the evaluation whose evidence card is expanded
  const [expandedEvaluationId, setExpandedEvaluationId] = useState<string | null>(null)
  // A status change awaiting confirmation: the tag shows the new value while an
  // inline comment box is open. Nothing is persisted until the user hits Update;
  // Discard clears this and the tag reverts to the saved value.
  const [pendingStatusChange, setPendingStatusChange] = useState<{ id: string; result: EvaluationResult } | null>(null)
  const [statusComment, setStatusComment] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close the popup: play the exit animation, then navigate back out of it
  const handleClose = () => {
    if (isClosing) return
    setIsClosing(true)
    setTimeout(() => {
      navigate(`/projects/${projectId}?tab=responses`)
    }, 200)
  }

  // Close on Escape, like a modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosing])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowFileDropdown(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Function to highlight text in PDF
  const highlightEvidenceInPDF = (evaluation: Evaluation) => {
    console.log('highlightEvidenceInPDF called:', {
      hasEvidence: !!evaluation.evidence,
      sourcePage: evaluation.source_page,
      sourceFilePath: evaluation.source_file_path
    })

    // Allow navigation even without source_page if we have source_file_path
    if (evaluation.evidence || evaluation.source_file_path) {
      trackEvaluationEvidenceViewed(evaluation.id, { source_page: evaluation.source_page })
      const highlight = {
        id: evaluation.id,
        text: evaluation.evidence || '',
        startIndex: evaluation.text_start_index || 0,
        length: evaluation.text_length || (evaluation.evidence?.length || 0),
        page: evaluation.source_page || 1
      }

      // Clear existing highlights first to ensure fresh state
      setPdfHighlights([])

      // Small delay to ensure the component processes the cleared state
      setTimeout(() => {
        processHighlight()
      }, 50)

      const processHighlight = () => {
        // Switch to the correct file if evaluation has source_file_path
        if (evaluation.source_file_path && vendorResponse?.files) {
          console.log('Looking for file:', evaluation.source_file_path)
          console.log('Available files:', vendorResponse.files.map(f => ({ name: f.file_name, path: f.file_path })))

          const fileIndex = vendorResponse.files.findIndex(file => {
            // Try exact filename match first
            if (file.file_name === evaluation.source_file_path) {
              console.log('Found exact filename match:', file.file_name)
              return true
            }

            // Try exact file path match
            if (file.file_path === evaluation.source_file_path) {
              console.log('Found exact path match:', file.file_path)
              return true
            }

            // Try partial path match (source_file_path contained in file_path)
            if (file.file_path.includes(evaluation.source_file_path)) {
              console.log('Found partial path match:', file.file_path, 'contains', evaluation.source_file_path)
              return true
            }

            // Try case-insensitive filename match
            if (file.file_name.toLowerCase() === evaluation.source_file_path.toLowerCase()) {
              console.log('Found case-insensitive filename match:', file.file_name)
              return true
            }

            return false
          })

          console.log('File search result - index:', fileIndex, 'current index:', selectedFileIndex)

          if (fileIndex !== -1 && fileIndex !== selectedFileIndex) {
            console.log('Switching to file index:', fileIndex, 'for file:', evaluation.source_file_path)

            // First switch the file
            setSelectedFileIndex(fileIndex)

            // Then wait for the PDF viewer to load the new file before setting page
            setTimeout(() => {
              setPdfHighlights([highlight])
              setCurrentPage(evaluation.source_page || 1)
              console.log('Set page and highlights after file switch:', evaluation.source_page || 1)
            }, 1500) // Give more time for the new PDF to load
          } else if (fileIndex !== -1) {
            // Same file, can set immediately
            console.log('Setting page immediately (same file):', evaluation.source_page || 1)
            setPdfHighlights([highlight])
            setCurrentPage(evaluation.source_page || 1)
          } else {
            console.warn('Could not find matching file for:', evaluation.source_file_path)
            // Still jump to the page on current file as fallback
            setPdfHighlights([highlight])
            setCurrentPage(evaluation.source_page || 1)
          }
        } else {
          // No source file path - this should not happen with proper evaluations
          console.error('MISSING source_file_path in evaluation:', evaluation)
          console.log('Setting page (no source file path):', evaluation.source_page || 1)
          setPdfHighlights([highlight])
          setCurrentPage(evaluation.source_page || 1)
        }

        // Log for debugging
        console.log('Highlighting evidence:', {
          evaluationId: evaluation.id,
          evidence: evaluation.evidence,
          evidenceLength: evaluation.evidence?.length,
          page: evaluation.source_page,
          startIndex: evaluation.text_start_index,
          length: evaluation.text_length,
          sourceFilePath: evaluation.source_file_path,
          requirement: evaluation.requirement
        })
      }
    } else {
      console.warn('Cannot navigate - no evidence or source_file_path:', evaluation)
    }
  }
  
  // Initialize AI Chat
  const chat = useChat({
    projectId: projectId!,
    vendorResponseId: responseId!,
    userId: 'user_1', // TODO: Get from authentication context
    useWebSocket: false // Temporarily disabled WebSocket, using HTTP fallback
  })

  // Initialize evaluation update hook
  const updateEvaluation = useUpdateEvaluation()

  // AI Assistant action handlers
  const handleSummarizeQualifications = async () => {
    await chat.summarizeQualifications()
  }

  const handleListMissingRequirements = async () => {
    await chat.listMissingRequirements()
  }

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // A status change opens an inline comment box instead of saving immediately, so
  // the reviewer can (optionally) note why. Selecting the current value is a no-op.
  const beginStatusChange = (evaluationId: string, currentResult: string, newResult: EvaluationResult) => {
    // Re-selecting the saved value cancels any pending change for this row.
    if (newResult === currentResult) {
      if (pendingStatusChange?.id === evaluationId) discardStatusChange()
      return
    }
    setPendingStatusChange({ id: evaluationId, result: newResult })
    setStatusComment('')
  }

  const discardStatusChange = () => {
    setPendingStatusChange(null)
    setStatusComment('')
  }

  // Persist the pending status change. The reviewer's comment becomes the decision
  // one-liner; with no comment we fall back to a clear "manually set" note.
  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return
    const { id, result } = pendingStatusChange
    const label = RESULT_META[result]?.label || result
    const summary = statusComment.trim() || `Manually set to "${label}" by reviewer`
    try {
      await updateEvaluation.mutateAsync({
        evaluationId: id,
        data: { result, decision_summary: summary }
      })
      setPendingStatusChange(null)
      setStatusComment('')
    } catch (error) {
      console.error('Failed to update evaluation:', error)
    }
  }

  // Fetch vendor response details
  const { data: vendorResponse, isLoading } = useQuery(
    ['vendorResponse', responseId],
    () => vendorsApi.get(responseId!),
    {
      enabled: !!responseId,
    }
  )

  // Fetch project details
  const { data: project } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId!),
    {
      enabled: !!projectId,
    }
  )

  // Fetch evaluations for this response
  const { data: evaluations, isLoading: isLoadingEvaluations } = useQuery(
    ['evaluations', responseId],
    () => evaluationsApi.getResponseEvaluations(responseId!),
    {
      enabled: !!responseId,
    }
  )


  const getEvaluationIcon = (result: string) => {
    switch (result) {
      case 'meets':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'partially_meets':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />
      case 'not_met':
        return <XCircle className="w-4 h-4 text-red-600" />
      default:
        return null
    }
  }

  const getEvaluationBadge = (result: string) => {
    switch (result) {
      case 'meets':
        return <Badge variant="success">Yes</Badge>
      case 'partially_meets':
        return <Badge variant="warning">Partially</Badge>
      case 'not_met':
        return <Badge variant="destructive">No</Badge>
      default:
        return null
    }
  }

  // Get overall status from backend (uses fit score calculation)
  const getOverallStatusDisplay = () => {
    const backendStatus = vendorResponse?.overall_requirement_status || 'pending'

    switch (backendStatus) {
      case 'meets':
        return {
          status: 'meets',
          label: 'Meets Requirements',
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          iconColor: 'text-green-600'
        }
      case 'partially_meets':
        return {
          status: 'partially_meets',
          label: 'Partially Meets Requirements',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600'
        }
      case 'not_met':
        return {
          status: 'not_met',
          label: 'Does Not Meet Requirements',
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          iconColor: 'text-red-600'
        }
      default:
        return {
          status: 'pending',
          label: 'Pending Evaluation',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          iconColor: 'text-gray-500'
        }
    }
  }

  const overallStatus = getOverallStatusDisplay()

  // Get MIME type based on file extension
  const getMimeType = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase()
    const mimeTypes: Record<string, string> = {
      'pdf': 'application/pdf',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'xls': 'application/vnd.ms-excel',
      'xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
      'csv': 'text/csv',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'doc': 'application/msword'
    }
    return mimeTypes[ext || ''] || 'application/octet-stream'
  }

  // Fetch file as blob and create object URL
  const loadFileAsBlob = async (filePath: string, fileName: string) => {
    setIsLoadingFile(true)
    try {
      // Remove 'uploads/' prefix if it exists since the streaming endpoint expects relative path
      const cleanPath = filePath.startsWith('uploads/') ? filePath.substring(8) : filePath

      // Use the configured API instance which includes auth interceptors
      const response = await api.get(`/files/stream/${cleanPath}`, {
        responseType: 'blob'
      })

      // Get MIME type based on file name
      const mimeType = getMimeType(fileName)

      const blob = new Blob([response.data], { type: mimeType })
      const url = URL.createObjectURL(blob)
      setFileBlobUrl(url)
    } catch (error) {
      console.error('Error loading file:', error)
      setFileBlobUrl(null)
    } finally {
      setIsLoadingFile(false)
    }
  }


  // Load file when selected file changes
  useEffect(() => {
    if (vendorResponse?.files?.[selectedFileIndex]) {
      const file = vendorResponse.files[selectedFileIndex]
      loadFileAsBlob(file.file_path, file.file_name)
      // Reset controls when switching files
      setCurrentPage(1)
      setPdfHighlights([])
    }
    
    // Cleanup previous blob URL
    return () => {
      if (fileBlobUrl) {
        URL.revokeObjectURL(fileBlobUrl)
      }
    }
  }, [selectedFileIndex, vendorResponse])

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (fileBlobUrl) {
        URL.revokeObjectURL(fileBlobUrl)
      }
    }
  }, [])


  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div
      className={`fixed inset-0 z-[100] bg-black/40 ${isClosing ? 'popup-backdrop--closing' : 'popup-backdrop'}`}
      onClick={handleClose}
    >
      <div
        className={`absolute inset-0 bg-[#fdfefe] flex flex-col overflow-hidden ${isClosing ? 'popup-panel--closing' : 'popup-panel'}`}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Header */}
      <div className="bg-white border-b border-[#ecf0f1] h-[104px] flex items-center px-6 gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleClose}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>

        {/* Vendor name as main title, bid name below as description */}
        <div className="flex flex-col min-w-0">
          <h1 className="text-xl font-bold text-[#303030] leading-tight truncate">
            {vendorResponse?.vendor_name || 'Loading...'}
          </h1>
          <p className="text-sm text-gray-500 truncate">
            {project?.title || 'Loading...'}
          </p>
        </div>

        {/* File selector in the middle */}
        <div className="flex-1 flex justify-center">
          <div className="relative" ref={dropdownRef}>
            <div>
              <button
                className="flex items-center gap-2 text-sm font-medium border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 min-w-[260px]"
                onClick={() => setShowFileDropdown(!showFileDropdown)}
              >
                <FileText className="w-4 h-4 shrink-0 text-gray-500" />
                <span className="truncate">
                  {vendorResponse?.files?.[selectedFileIndex]?.file_name || 'Loading...'}
                </span>
                <ChevronDown className="w-4 h-4 ml-auto shrink-0" />
              </button>

              {showFileDropdown && vendorResponse?.files && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[300px] max-h-80 overflow-y-auto">
                  {vendorResponse.files.map((file: FileItem, index: number) => (
                    <button
                      key={index}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                        selectedFileIndex === index ? 'bg-[#f3ebfb]' : ''
                      }`}
                      onClick={() => {
                        setSelectedFileIndex(index)
                        setShowFileDropdown(false)
                      }}
                    >
                      <div className="font-medium text-sm">{file.file_name}</div>
                      <div className="text-xs text-gray-500">
                        {Math.round(file.file_size / 1024 / 1024 * 100) / 100} MB
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`${overallStatus.bgColor} px-2 py-1 rounded flex items-center gap-2 shrink-0`}>
          {overallStatus.status === 'meets' && <CheckCircle className={`w-4 h-4 ${overallStatus.iconColor}`} />}
          {overallStatus.status === 'partially_meets' && <AlertTriangle className={`w-4 h-4 ${overallStatus.iconColor}`} />}
          {overallStatus.status === 'not_met' && <XCircle className={`w-4 h-4 ${overallStatus.iconColor}`} />}
          {overallStatus.status === 'pending' && <AlertTriangle className={`w-4 h-4 ${overallStatus.iconColor}`} />}
          <span className={`text-xs font-bold ${overallStatus.textColor}`}>{overallStatus.label}</span>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* PDF Viewer Section */}
        <div className="w-[65%] bg-slate-50 relative min-h-0">

          {/* PDF Viewer */}
          <div className="w-full h-full relative">
            {isLoadingFile ? (
              <div className="bg-white rounded border border-[#b2bbbc] shadow-lg h-full flex items-center justify-center">
                <div className="text-center p-6">
                  <LoadingSpinner size="lg" />
                  <p className="text-center text-gray-600 mt-4">Loading file...</p>
                </div>
              </div>
            ) : fileBlobUrl ? (
              <FileViewer
                fileUrl={fileBlobUrl}
                fileName={vendorResponse?.files?.[selectedFileIndex]?.file_name}
                fileType={vendorResponse?.files?.[selectedFileIndex]?.file_type}
                highlights={pdfHighlights}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onHighlightClick={(highlight) => {
                  console.log('Highlight clicked:', highlight)
                }}
              />
            ) : (
              <div className="bg-white rounded border border-[#b2bbbc] shadow-lg h-full flex items-center justify-center">
                <div className="text-center p-6">
                  <div className="w-24 h-24 mx-auto mb-6">
                    <div className="w-full h-full bg-blue-100 rounded-full flex items-center justify-center">
                      <FileText className="w-12 h-12 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 text-center mb-3">No File Available</h3>
                  <p className="text-center text-gray-600">
                    Unable to load the selected file
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-[35%] bg-white border-l border-[#ecf0f1] flex flex-col min-h-0">
          {/* Evaluations content */}
          <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 min-h-0 overflow-y-auto p-6">
                {isLoadingEvaluations ? (
                  <div className="flex items-center justify-center h-32">
                    <LoadingSpinner size="md" />
                  </div>
                ) : (
                  <>
                    <h3 className="text-sm font-medium text-gray-900 mb-4">
                      {evaluations?.length || 0} Evaluations
                    </h3>
                    <div>
                      <table className="w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Requirement
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {evaluations?.slice().reverse().map((evaluation) => {
                            const hasDetails = !!(evaluation.evidence || evaluation.reasoning || evaluation.decision_summary)
                            const isExpanded = expandedEvaluationId === evaluation.id
                            return (
                            <React.Fragment key={evaluation.id}>
                            <tr className="hover:bg-gray-50 align-top">
                              {/* Requirement column: clicking anywhere here opens/toggles the details card. */}
                              <td
                                className={`px-3 py-2 text-left ${hasDetails ? 'cursor-pointer' : ''}`}
                                onClick={hasDetails ? () => setExpandedEvaluationId(isExpanded ? null : evaluation.id) : undefined}
                              >
                                <div className="flex items-center gap-1.5">
                                  <div className="text-xs font-medium text-gray-900">
                                    {evaluation.requirement?.title || evaluation.requirement?.requirement_id}
                                  </div>
                                  {hasDetails && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setExpandedEvaluationId(isExpanded ? null : evaluation.id) }}
                                      aria-expanded={isExpanded}
                                      aria-label={isExpanded ? 'Hide evaluation details' : 'Show evaluation details'}
                                      className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                    >
                                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                  )}
                                </div>
                                {evaluation.decision_summary && (
                                  <div className="mt-0.5 text-[11px] leading-snug text-gray-500">
                                    {evaluation.decision_summary}
                                  </div>
                                )}
                              </td>
                              {/* Status column: interacting here collapses any open details card. */}
                              <td className="px-3 py-2 text-center" onClick={() => setExpandedEvaluationId(null)}>
                                <div className="inline-flex items-center justify-center">
                                  {evaluation.submission_status !== 'submitted' ? (
                                    <span className="text-[11px] italic text-red-600">Not submitted</span>
                                  ) : (
                                    <StatusResultDropdown
                                      value={pendingStatusChange?.id === evaluation.id ? pendingStatusChange.result : evaluation.result}
                                      onChange={(result) => beginStatusChange(evaluation.id, evaluation.result, result)}
                                    />
                                  )}
                                </div>
                              </td>
                              {/* Actions column: View (and any click here) collapses the open card. */}
                              <td className="px-3 py-2 text-center" onClick={() => setExpandedEvaluationId(null)}>
                                <div className="flex items-center justify-center">
                                  {/* View document button - show when section was submitted */}
                                  {evaluation.submission_status === 'submitted' && (evaluation.source_file_path || (evaluation.mapped_files?.length ?? 0) > 0) ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs px-2 py-1"
                                      onClick={() => {
                                        highlightEvidenceInPDF(evaluation)
                                      }}
                                    >
                                      View
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-gray-400 w-12">-</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {pendingStatusChange?.id === evaluation.id && (
                              <tr>
                                <td colSpan={3} className="px-3 pt-0 pb-3 bg-gray-50">
                                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                      Comment (optional)
                                    </div>
                                    <textarea
                                      value={statusComment}
                                      onChange={(e) => setStatusComment(e.target.value)}
                                      rows={2}
                                      autoFocus
                                      placeholder={`Why "${RESULT_META[pendingStatusChange.result]?.label || pendingStatusChange.result}"? Leave blank to note it was set manually.`}
                                      className="w-full resize-none rounded border border-gray-200 p-2 text-xs leading-relaxed text-gray-800 focus:border-[#7035c4] focus:outline-none focus:ring-1 focus:ring-[#7035c4]"
                                    />
                                    <div className="mt-2 flex items-center justify-end gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-xs px-2 py-1"
                                        onClick={discardStatusChange}
                                        disabled={updateEvaluation.isLoading}
                                      >
                                        Discard
                                      </Button>
                                      <Button
                                        size="sm"
                                        className="text-xs px-2 py-1"
                                        onClick={confirmStatusChange}
                                        disabled={updateEvaluation.isLoading}
                                      >
                                        Update
                                      </Button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                            {hasDetails && isExpanded && (
                              <tr>
                                <td colSpan={3} className="px-3 pt-0 pb-3 bg-gray-50">
                                  <div className="rounded-lg border border-gray-200 bg-white p-3 max-h-64 overflow-y-auto">
                                    {evaluation.decision_summary && (
                                      <p className="mb-3 text-xs font-semibold leading-relaxed text-gray-900">
                                        {evaluation.decision_summary}
                                      </p>
                                    )}
                                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Evidence</div>
                                    <p className="mb-3 whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-800">
                                      {evaluation.evidence || 'No evidence provided'}
                                    </p>
                                    {evaluation.reasoning && (
                                      <>
                                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reasoning</div>
                                        <p className="mb-3 whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-800">
                                          {evaluation.reasoning}
                                        </p>
                                      </>
                                    )}
                                    {evaluation.confidence_score != null && (
                                      <div className="text-[11px] font-medium text-gray-500">
                                        Confidence: {Math.round(evaluation.confidence_score * 100)}%
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

export default VendorResponseDetailPage